export interface LazyVideoOptions {
  /** 提前多少开始加载，默认 240px */
  rootMargin?: string
  /** 进入视口后是否自动播放（静音） */
  autoplay?: boolean
  /** 串行加载，避免移动端/微信并发抢带宽导致黑屏 */
  sequential?: boolean
  /** 非自动播放视频加载后尝试解码首帧（微信等环境） */
  showFirstFrame?: boolean
  /** autoplay 失败时的回调（用于挂起等待微信/手势解锁） */
  onAutoplayBlocked?: (video: HTMLVideoElement, error?: unknown) => void
  /** 不等 IntersectionObserver，立刻开始加载（适合需要滑动解锁前就有 src） */
  eager?: boolean
}

/** 全页共享的视频加载队列，同一时刻只拉一个大文件 */
let loadChain: Promise<void> = Promise.resolve()

const MEDIA_START_TIMEOUT = 4000

function enqueueLoad(task: () => Promise<void>): Promise<void> {
  const run = loadChain.then(task, task)
  // 队列不因单个失败中断
  loadChain = run.catch(() => undefined)
  return run
}

function waitForMediaEvent(video: HTMLVideoElement, timeout = MEDIA_START_TIMEOUT): Promise<void> {
  if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) return Promise.resolve()

  return new Promise<void>((resolve) => {
    let timer = 0
    const done = () => {
      window.clearTimeout(timer)
      video.removeEventListener('loadeddata', done)
      video.removeEventListener('error', done)
      resolve()
    }

    timer = window.setTimeout(done, timeout)
    video.addEventListener('loadeddata', done, { once: true })
    video.addEventListener('error', done, { once: true })
  })
}

async function tryAutoplay(video: HTMLVideoElement): Promise<{ playing: boolean; error?: unknown }> {
  try {
    // 必须在设置 src 后立即调用 play()：微信里若先 await loadeddata，
    // preload="none" 可能一直不解码，形成“等播放才加载，等加载才播放”的循环。
    const playPromise = video.play()
    await Promise.race([
      playPromise,
      new Promise<void>((resolve) => window.setTimeout(resolve, MEDIA_START_TIMEOUT))
    ])
    return { playing: !video.paused }
  } catch (error) {
    return { playing: false, error }
  }
}

async function tryShowFirstFrame(video: HTMLVideoElement): Promise<boolean> {
  try {
    video.muted = true
    video.defaultMuted = true
    video.setAttribute('muted', '')
    // 微信等 WebView 常需 play 才能解码出首帧
    await video.play()
    return !video.paused
  } catch {
    return false
  }
}

/**
 * 进入视口附近再写入 video.src，避免首屏并发拉取多个大视频。
 * 返回 disconnect 清理函数。
 */
export function bindLazyVideo(
  video: HTMLVideoElement | null | undefined,
  src: string,
  options: LazyVideoOptions = {}
): () => void {
  if (!video || !src) return () => undefined

  const {
    rootMargin = '240px 0px',
    autoplay = false,
    sequential = false,
    showFirstFrame = false,
    onAutoplayBlocked,
    eager = false
  } = options

  let cancelled = false

  const load = () => {
    if (cancelled || video.dataset.lazyLoaded === '1') return Promise.resolve()
    video.dataset.lazyLoaded = '1'

    const doLoad = async () => {
      if (cancelled) return
      video.muted = true
      video.defaultMuted = true
      video.setAttribute('muted', '')
      video.src = src
      video.load()

      if (autoplay) {
        const result = await tryAutoplay(video)
        if (cancelled || result.playing) return

        // 微信拦截后由 Bridge/手势队列重试。此时不再等待 loadeddata，
        // 否则后续串行视频也会被第一个视频卡住。
        onAutoplayBlocked?.(video, result.error)
        return
      }

      if (showFirstFrame) {
        await waitForMediaEvent(video)
        if (cancelled) return
        const playing = await tryShowFirstFrame(video)
        if (playing) {
          video.pause()
          if (video.currentTime < 0.05) video.currentTime = 0.05
        }
      }
    }

    return sequential ? enqueueLoad(doLoad) : doLoad()
  }

  if (eager || !('IntersectionObserver' in window)) {
    void load()
    return () => {
      cancelled = true
    }
  }

  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return
      void load()
      observer.disconnect()
    },
    { rootMargin, threshold: 0.01 }
  )
  observer.observe(video)
  return () => {
    cancelled = true
    observer.disconnect()
  }
}

/** 主动写入 src（点击播放时兜底），可选走串行队列 */
export function ensureVideoSource(
  video: HTMLVideoElement | null | undefined,
  src: string,
  options: { sequential?: boolean } = {}
): Promise<void> {
  if (!video || !src) return Promise.resolve()
  if (video.dataset.lazyLoaded === '1' && video.src) return Promise.resolve()

  const doLoad = async () => {
    video.dataset.lazyLoaded = '1'
    video.src = src
    video.load()
    await waitForMediaEvent(video)
  }

  return options.sequential ? enqueueLoad(doLoad) : doLoad()
}
